import { useState } from "react";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Loader2, RotateCw, Plus, Minus, SlidersHorizontal, Route, Scale, Clock } from "lucide-react";

const FACTORS = [
  { key: "distance", label: "Shortest driving", icon: Route },
  { key: "workload", label: "Even workload", icon: Scale },
  { key: "ontime", label: "On-time reliability", icon: Clock },
];

// Move 5 points into (or out of) one factor, balancing against the others so the total stays 100.
export function nudge(w, key, delta) {
  const next = { ...w };
  const target = Math.max(0, Math.min(100, next[key] + delta));
  let diff = target - next[key];
  next[key] = target;
  const others = FACTORS.map((f) => f.key).filter((k) => k !== key).sort((a, b) => (diff > 0 ? next[b] - next[a] : next[a] - next[b]));
  for (const k of others) {
    if (!diff) break;
    const take = diff > 0 ? Math.min(diff, next[k]) : Math.max(diff, next[k] - 100);
    next[k] -= take; diff -= take;
  }
  if (diff) next[key] -= diff;
  return next;
}

const Stepper = ({ f, value, onChange }) => (
  <div className="flex items-center gap-3" data-testid={`ai-weight-${f.key}`}>
    <f.icon className="h-4 w-4 shrink-0 text-blue-300" />
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-white">{f.label}</p>
      <div className="mt-1 h-1.5 rounded-full bg-slate-800"><div className="h-1.5 rounded-full bg-blue-500 transition-[width] duration-200" style={{ width: `${value}%` }} /></div>
    </div>
    <button type="button" onClick={() => onChange(-5)} disabled={value <= 0} data-testid={`ai-weight-${f.key}-minus`} className="rounded-md border border-slate-700 p-1.5 text-slate-200 transition-colors hover:bg-slate-800 disabled:opacity-40"><Minus className="h-3.5 w-3.5" /></button>
    <span className="w-10 text-center font-mono-data text-sm font-semibold text-white" data-testid={`ai-weight-${f.key}-value`}>{value}%</span>
    <button type="button" onClick={() => onChange(5)} disabled={value >= 100} data-testid={`ai-weight-${f.key}-plus`} className="rounded-md border border-slate-700 p-1.5 text-slate-200 transition-colors hover:bg-slate-800 disabled:opacity-40"><Plus className="h-3.5 w-3.5" /></button>
  </div>
);

export function PriorityPanel({ presets, weights, setWeights, onPresetsChanged, onPlan, busy, hasPlan, disabled, autoAccept, setAutoAccept }) {
  const [show, setShow] = useState(true);
  const [saving, setSaving] = useState(false);
  const same = (p) => FACTORS.every((f) => p.weights?.[f.key] === weights[f.key]);

  const saveCurrent = async () => {
    const name = window.prompt("Name this priority mix (e.g. On-time first):");
    if (!name?.trim()) return;
    setSaving(true);
    try {
      await fleetApi.post("/assign-presets", { name: name.trim(), weights });
      toast.success(`Saved “${name.trim()}”.`);
      onPresetsChanged();
    } catch (e) { toast.error(fleetError(e, "Couldn't save this mix.")); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 rounded-xl border border-blue-600/30 bg-blue-600/5 p-4" data-testid="ai-priority-panel">
      <button type="button" onClick={() => setShow((s) => !s)} className="flex items-center gap-1.5 text-sm font-semibold text-blue-300 hover:text-blue-200" data-testid="ai-priority-toggle">
        <SlidersHorizontal className="h-4 w-4" /> {show ? "Hide priorities" : "Show priorities"}
      </button>
      {show && (
        <>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Presets</p>
            <div className="flex flex-wrap gap-2">
              {presets.map((p) => (
                <button key={p.id} type="button" onClick={() => setWeights({ ...p.weights })} data-testid={`ai-preset-${p.id}`}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${same(p) ? "border-blue-500 bg-blue-600 text-white" : "border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"}`}>
                  {p.name}
                </button>
              ))}
              <button type="button" onClick={saveCurrent} disabled={saving} data-testid="ai-preset-save"
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-blue-500/60 px-3.5 py-1.5 text-xs font-semibold text-blue-300 transition-colors hover:bg-blue-600/10">
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Save current
              </button>
            </div>
          </div>
          <div className="space-y-3">
            {FACTORS.map((f) => <Stepper key={f.key} f={f} value={weights[f.key]} onChange={(d) => setWeights(nudge(weights, f.key, d))} />)}
          </div>
        </>
      )}
      <button type="button" onClick={onPlan} disabled={busy || disabled} data-testid="ai-generate-btn"
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-700 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-800 disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />} {hasPlan ? "Apply & re-plan" : "Build plan"}
      </button>
      <div className="flex items-center justify-between gap-4 border-t border-slate-800 pt-3">
        <div>
          <p className="text-sm font-semibold text-white">Auto-accept plan</p>
          <p className="text-xs text-slate-400">For busy mornings — assign as soon as the plan is ready. Wheelchair mismatches are never auto-assigned.</p>
        </div>
        <Switch checked={autoAccept} onCheckedChange={setAutoAccept} data-testid="ai-auto-accept-switch" />
      </div>
    </div>
  );
}
